import { useState, useMemo, useEffect, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import {
  useGetEnrollmentJointReenterVerifyPhoneData,
  useSubmitEnrollmentJointReenterVerifyPhoneData,
  useSubmitEnrollmentJointResendReenterVerifyPhoneData,
  useSubmitEnrollmentJointReenterVerifyPhoneOtpData,
} from '@egw/api'
import { Alert, AlertType, GenericError, OtpInput } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentJointLayout } from '@egw/layouts'
import { CheckIcon } from '@heroicons/react/24/outline'
import { useForm } from 'react-hook-form'
import { useRouter } from '@egw/router'
import { useLocation } from 'react-router'
import { formatPhoneNumber } from '../../../utils/phone-number/phone-number'
import { PageHeader } from '../../../components'
import { enrollmentCookieService } from '@egw/enrollment-cookie'

const log = new Logger('Enrollment - Joint - ReenterVerifyPhone')

export const ReenterVerifyPhonePage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()
  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const token = query.get('token') ?? ''
  const [isInvalidToken, setIsInvalidToken] = useState(false)

  // Token verification logic - extracted from URL parameters
  // The token can be used for magic link verification
  const dataLoadPayload = useMemo(
    () => ({
      emailMagicLinkToken: token || undefined,
    }),
    [token],
  )

  const { data, isLoading, error } = useGetEnrollmentJointReenterVerifyPhoneData(dataLoadPayload)
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointReenterVerifyPhoneData()
  const {
    mutate: resend,
    isLoading: _isResendSubmitting,
    error: submitResendError,
  } = useSubmitEnrollmentJointResendReenterVerifyPhoneData()

  const {
    mutate: continueApplication,
    isLoading: isContinuingApplication,
    error: errorContinuingApplication,
  } = useSubmitEnrollmentJointReenterVerifyPhoneOtpData()

  // Reset enrollment session when entering this page
  useEffect(() => {
    void enrollmentCookieService.resetSession()
  }, [])

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint reenter verify phone data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint reenter verify phone form', submitError)
    }
  }, [submitError])

  // Log resend errors
  useEffect(() => {
    if (submitResendError) {
      log.error('Error resending enrollment joint reenter verify phone', submitResendError)
    }
  }, [submitResendError])

  // Log continue application errors
  useEffect(() => {
    if (errorContinuingApplication) {
      log.error('Error continuing application after phone verification', errorContinuingApplication)
    }
  }, [errorContinuingApplication])

  const [codeResent, setCodeResent] = useState(false)
  const [counter, setCounter] = useState(0)
  const [otpValue, setOtpValue] = useState('')

  const handleOtpComplete = (otp: string): void => {
    void onSubmit(otp)
  }

  const handleOtpChange = (value: string): void => {
    const trimmedValue = value.slice(0, 6)
    setOtpValue(trimmedValue)
  }

  const phoneNumber = data?.phone.phoneNumber ?? ''

  const {
    formState: { errors: _formErrors, isValid },
  } = useForm({
    defaultValues: {
      code: '',
    },
  })

  const resendOtp = async (): Promise<void> => {
    setCodeResent(true)

    try {
      await resend({
        triggerPhoneOtp: true,
      })

      setTimeout(() => {
        setCounter(30)
        const intervalId = setInterval(() => {
          setCounter((prev) => {
            if (prev <= 1) {
              clearInterval(intervalId)
              setCodeResent(false)

              return 0
            }
            return prev - 1
          })
        }, 1000)
      }, 5000)
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const onSubmit = async (otpCode: string): Promise<void> => {
    if (isValid) {
      setIsInvalidToken(false)
      try {
        const response = await mutate({
          phoneVerificationCode: otpCode,
        })

        if (response?.error === 'INVALID_VERIFICATION_CODE') {
          setIsInvalidToken(true)
        } else if (response?.nextStep === 'SESSION_RESTORED') {
          // Continue application
          const responseContinueApplication = await continueApplication(undefined)
          if (responseContinueApplication?.activePerson?.path) {
            navigate(responseContinueApplication.activePerson.path)
          }
        }
      } catch (e) {
        log.error('Error submitting', e)
      }
    }
  }

  return (
    <EnrollmentJointLayout title={t('common.verify_phone_v2.otp.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          <PageHeader title={t('common.verify_phone_v2.otp.title')} />
          <p className="typo-primary-base-regular text-text-meta mb-3xs">{t('common.verify_phone_v2.otp.sent_to')}</p>
          <p className="typo-primary-base-semibold text-text-header mb-md">{formatPhoneNumber(phoneNumber || '')}</p>

          <OtpInput
            value={otpValue}
            onChange={handleOtpChange}
            onComplete={handleOtpComplete}
            className="mb-sm"
            testId="otp-input"
            error={isInvalidToken ? t('common.verify_phone_v2.otp.code_expired') : ''}
          />

          <div className="flex items-baseline gap-4">
            <button
              onClick={() => void resendOtp()}
              disabled={codeResent || isSubmitting || isContinuingApplication}
              className={`typo-primary-sm-semibold text-left w-fit ${codeResent ? 'text-gray-gray-600' : ' text-text-action'}`}
              type="button"
            >
              {t('common.verify_phone_v2.otp.resend_message')}
            </button>
            {codeResent && !counter ? (
              <p className="typo-primary-sm-semibold text-text-body-success flex items-center gap-2">
                {t('common.verify_phone_v2.otp.message_sent')} <CheckIcon className="size-5" />
              </p>
            ) : null}
          </div>

          {counter ? (
            <p className="typo-primary-sm-semibold text-text-placeholder mt-2xs">
              {t('common.verify_phone_v2.otp.send_another_message', {
                time: counter,
              })}
            </p>
          ) : null}

          <div className="flex-1 md:hidden" />

          {submitError || errorContinuingApplication ? (
            <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
          ) : null}

          {/* AUTO GENERATED COMPONENTS */}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
